import { PayloadAction } from '@reduxjs/toolkit';
import dotPropImmutable from 'dot-prop-immutable';
import { Comment } from 'api/conversations';
import { ConversationMember } from 'api/project-conversations';
import { ConversationContext, ConversationStore } from '../types';

const PER_PAGE = 20;

export const conversationSlice = {
  fetchStart: (
    state: ConversationStore,
    {
      payload,
    }: PayloadAction<{
      context: ConversationContext;
      projectId: number;
    }>,
  ): ConversationStore => {
    const { context, projectId } = payload;

    return dotPropImmutable.merge(state, `${context.type}-${context.id}`, {
      context,
      comments: [],
      allComments: [],
      projectId,
      hasLoadedAllComments: false,
      loading: true,
      error: false,
    });
  },

  fetchSuccess: (
    state: ConversationStore,
    {
      payload,
    }: PayloadAction<{
      context: ConversationContext;
      conversation: Comment[];
      members: ConversationMember[];
    }>,
  ): ConversationStore => {
    const { context, conversation, members } = payload;

    return dotPropImmutable.merge(state, `${context.type}-${context.id}`, {
      allComments: conversation,
      members,
      comments: conversation.slice(-PER_PAGE),
      loading: false,
      error: false,
      hasLoadedAllComments: conversation.length <= PER_PAGE,
    });
  },

  fetchError: (
    state: ConversationStore,
    {
      payload,
    }: PayloadAction<{
      context: ConversationContext;
    }>,
  ): ConversationStore => {
    const { context } = payload;

    return dotPropImmutable.merge(state, `${context.type}-${context.id}`, {
      allComments: [],
      comments: [],
      loading: false,
      error: true,
    });
  },

  getAllComments: (
    state: ConversationStore,
    {
      payload,
    }: PayloadAction<{
      context: ConversationContext;
    }>,
  ): ConversationStore => {
    const { context } = payload;

    const allComments = dotPropImmutable.get(
      state,
      `${context.type}-${context.id}.allComments`,
      [],
    );
    const comments = dotPropImmutable.get(
      state,
      `${context.type}-${context.id}.comments`,
      [],
    );

    return dotPropImmutable.merge(state, `${context.type}-${context.id}`, {
      comments: [
        ...allComments.slice(0, allComments.length - PER_PAGE),
        ...comments,
      ],
      hasLoadedAllComments: true,
    });
  },
  updateLastSeen: (
    state: ConversationStore,
    {
      payload,
    }: PayloadAction<{
      context: ConversationContext;
      members: ConversationMember[];
    }>,
  ): ConversationStore | undefined => {
    const { context, members } = payload;

    if (!state[`${context.type}-${context.id}`]) {
      return state;
    }
    state[`${context.type}-${context.id}`].members = members;

    return state;
  },
};
